import { CheckCircle } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { useWizardContext, Wizard, type WizardStep } from '@/lib/base/wizard';

import { useActiveCampaignImport } from '../hooks/useActiveCampaignImport';
import { useActiveCampaignMappings } from '../hooks/useActiveCampaignMappings';
import { useActiveCampaignStatus } from '../hooks/useActiveCampaignStatus';
import { useAvailableContactProperties } from '../hooks/useAvailableContactProperties';
import type {
  ActiveCampaignFieldMapping,
  ActiveCampaignListItem,
} from '../types';
import { getAcImportErrorMessage } from '../utils/importErrors';
import { ConnectActiveCampaignForm } from './ConnectActiveCampaignForm';
import { WizardFieldsStep } from './wizard/WizardFieldsStep';
import { WizardImportStep } from './wizard/WizardImportStep';

interface ActiveCampaignSetupWizardProps {
  orgId: number | string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Step to open on (computed from connection status by the parent). */
  defaultStepId: string;
  /** Called when the user closes the wizard from the done step. */
  onFinished?: () => void;
}

const EMPTY_MAPPINGS: ActiveCampaignFieldMapping[] = [];
const EMPTY_LISTS: ActiveCampaignListItem[] = [];

// One content width for the whole wizard so the body column AND the footer CTAs sit
// in the same place on every step. Hoisted to module scope so the object reference
// is stable across renders.
const WIZARD_FULLSCREEN_PROPS = { maxContentWidth: 900 } as const;

interface AcConnectStepProps {
  orgId: number | string;
  connected: boolean;
  onConnected: () => void;
}

function AcConnectStep({ orgId, connected, onConnected }: AcConnectStepProps) {
  // Advance to the fields step exactly once, the moment the status poll
  // reports connected=true. The ref guard makes this loop-safe even though
  // `connected` keeps getting re-delivered on every status refetch.
  const advanced = useRef(false);
  useEffect(() => {
    if (connected && !advanced.current) {
      advanced.current = true;
      onConnected();
    }
  }, [connected, onConnected]);

  if (!connected) {
    return <ConnectActiveCampaignForm orgId={orgId} />;
  }

  return (
    <div className='flex justify-center py-10'>
      <LoadingIndicator size='sm' />
    </div>
  );
}

// Small bridge component: it's the one that's actually rendered inside the
// Wizard's provider tree (via JSX in a step's `render`), so it's the only
// place that can call useWizardContext() to resolve `goTo`.
function ConnectStepBody({
  orgId,
  connected,
}: {
  orgId: number | string;
  connected: boolean;
}) {
  const { goTo } = useWizardContext();
  const onConnected = useCallback(() => goTo('fields'), [goTo]);

  return (
    <AcConnectStep
      orgId={orgId}
      connected={connected}
      onConnected={onConnected}
    />
  );
}

export function ActiveCampaignSetupWizard({
  orgId,
  isOpen,
  onOpenChange,
  defaultStepId,
  onFinished,
}: ActiveCampaignSetupWizardProps) {
  const status = useActiveCampaignStatus(orgId);
  const connected = status.data?.connected ?? false;

  // Field mappings are provisioned synchronously in the connect transaction —
  // no audience step to wait on, unlike Mailchimp. Gate only on the wizard
  // being open and the integration being connected.
  const { mappings, updateMapping, savingIds, errorMessagesById } =
    useActiveCampaignMappings(orgId, {
      enabled: isOpen && connected,
    });
  const availableAhoyProperties = useAvailableContactProperties(orgId);

  const allMappings = mappings.data?.mappings ?? EMPTY_MAPPINGS;

  const handleMappingUpdate = useCallback(
    (
      mappingId: number,
      body: Parameters<typeof updateMapping.mutate>[0]['body']
    ) => {
      updateMapping.mutate({ mappingId, body });
    },
    [updateMapping]
  );

  const handleClose = useCallback(() => {
    onOpenChange(false);
    onFinished?.();
  }, [onOpenChange, onFinished]);

  // Import step: default-list picker + opt-in one-time outbound seed of
  // existing Ahoy contacts. Lists are only meaningful once connected.
  const { lists, updateDefaultList, seedOutbound } = useActiveCampaignImport(
    orgId,
    { enabled: isOpen && connected }
  );
  const allLists = lists.data?.lists ?? EMPTY_LISTS;
  const [selectedListId, setSelectedListId] = useState('');
  const [importEnabled, setImportEnabled] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const handleSelectListChange = useCallback((listId: string) => {
    setSelectedListId(listId);
  }, []);
  const handleImportToggleChange = useCallback((enabled: boolean) => {
    setImportEnabled(enabled);
  }, []);
  const handleImportBeforeNext = useCallback(async () => {
    setImportError(null);
    try {
      if (selectedListId) {
        await updateDefaultList.mutateAsync(selectedListId);
      }
      if (importEnabled) {
        await seedOutbound.mutateAsync();
      }
    } catch (error) {
      setImportError(getAcImportErrorMessage(error));
      throw error;
    }
  }, [selectedListId, importEnabled, updateDefaultList, seedOutbound]);

  const steps = useMemo<WizardStep[]>(() => {
    return [
      {
        id: 'connect',
        label: 'Connect',
        // The body owns its CTAs: the connect form before connecting, then a
        // brief loading state until the wizard advances itself to Fields.
        hideFooter: true,
        render: () => <ConnectStepBody orgId={orgId} connected={connected} />,
      },
      {
        id: 'fields',
        label: 'Fields',
        title: 'Map your fields',
        subtitle:
          'Choose how each ActiveCampaign field maps to an Ahoy contact property.',
        hideBackButton: true,
        render: () => (
          <WizardFieldsStep
            mappings={allMappings}
            availableAhoyProperties={availableAhoyProperties}
            savingIds={savingIds}
            errorsById={errorMessagesById}
            isLoading={mappings.isLoading}
            isError={mappings.isError}
            onUpdate={handleMappingUpdate}
          />
        ),
        canAdvance: () => !mappings.isLoading && allMappings.length > 0,
        primaryCta: { label: 'Next' },
      },
      {
        id: 'import',
        label: 'Import',
        title: 'Import existing contacts',
        subtitle:
          'Choose a default list for new contacts, and optionally sync your existing Ahoy contacts to ActiveCampaign.',
        render: () => (
          <WizardImportStep
            lists={allLists}
            isLoading={lists.isLoading}
            isError={lists.isError}
            selectedListId={selectedListId}
            onSelectListChange={handleSelectListChange}
            importEnabled={importEnabled}
            onImportToggleChange={handleImportToggleChange}
            isSaving={updateDefaultList.isPending || seedOutbound.isPending}
            errorMessage={importError}
          />
        ),
        onBeforeNext: handleImportBeforeNext,
        primaryCta: { label: 'Next' },
      },
      {
        id: 'done',
        label: 'Done',
        hideFooter: true,
        render: () => (
          <div className='flex flex-col items-center gap-4 py-6 text-center'>
            <FeaturedIcon
              icon={CheckCircle}
              color='success'
              theme='light-outline'
              size='lg'
            />
            <div>
              <h3 className='text-heading-3 text-[var(--color-text-primary)]'>
                You’re all set
              </h3>
              <p className='mt-1 max-w-md text-body-small text-[var(--color-text-secondary)]'>
                ActiveCampaign is connected. Your contacts sync both ways, and
                your existing contacts are importing in the background.
              </p>
            </div>
            <Button color='primary' size='md' onClick={handleClose}>
              Go to ActiveCampaign settings
            </Button>
          </div>
        ),
      },
    ];
  }, [
    orgId,
    connected,
    allMappings,
    availableAhoyProperties,
    savingIds,
    errorMessagesById,
    mappings.isLoading,
    mappings.isError,
    handleMappingUpdate,
    handleClose,
    allLists,
    lists.isLoading,
    lists.isError,
    selectedListId,
    handleSelectListChange,
    importEnabled,
    handleImportToggleChange,
    updateDefaultList.isPending,
    seedOutbound.isPending,
    importError,
    handleImportBeforeNext,
  ]);

  return (
    <Wizard
      mode='fullscreen'
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      steps={steps}
      defaultStepId={defaultStepId}
      fullscreenProps={WIZARD_FULLSCREEN_PROPS}
      dialogAriaLabel='Set up ActiveCampaign'
      instance='activecampaign-setup-wizard'
      discardConfirmation={false}
    />
  );
}
